const libFableServiceProviderBase = require('fable-serviceproviderbase');

/**
 * Actions a dashboard can ask its host to take. Dashboards say what they want ("open sample 123"); the host decides
 * where that goes — a LIMS route, a native iPad screen, a new tab. Keep in step with the action table in
 * docs-internal/materials-dashboards/00-design.md and the iOS `dashboardAction` handler in ~/dev/mpi_ios.
 *
 * @type {Record<string, { Required: Array<string>, Optional?: Array<string> }>}
 */
const ACTIONS =
{
	'Sample.Open': { Required: [ 'IDSample' ] },
	'Sample.Create': { Required: [ 'IDProject', 'IDLineItem', 'IDMaterial' ], Optional: [ 'Quantity', 'SampleType' ] },
	'Sample.Receive': { Required: [ 'IDSample' ] },
	'Document.Open': { Required: [ 'IDDocument' ], Optional: [ 'IDSample' ] },
	'Project.Open': { Required: [ 'IDProject' ] },
	'Material.Open': { Required: [ 'IDMaterial' ] },
	'Dashboard.Open': { Required: [ 'Hash' ], Optional: [ 'IDProject', 'Filters' ] },
};

const NATIVE_HANDLER = 'dashboardAction';
const PARENT_MESSAGE_KEY = 'HeadlightDashboardAction';

/**
 * @typedef {Object} DashboardAction
 * @property {string} Action - A key of ACTIONS.
 */

/**
 * `data-*` attribute names arrive camel-cased (`data-id-line-item` → `idLineItem`); actions use HeadLight column
 * spelling (`IDLineItem`).
 *
 * @param {string} pKey
 * @return {string}
 */
function normalizeParamName(pKey)
{
	if (/^id[A-Z]/.test(pKey))
	{
		return `ID${pKey.slice(2)}`;
	}
	return pKey.charAt(0).toUpperCase() + pKey.slice(1);
}

/**
 * @param {string} pName
 * @param {string} pValue
 * @return {any}
 */
function normalizeParamValue(pName, pValue)
{
	if (pName === 'Filters')
	{
		try
		{
			return JSON.parse(pValue);
		}
		catch (pError)
		{
			return undefined;
		}
	}
	if ((/^ID[A-Z]/.test(pName) || pName === 'Quantity') && /^-?\d+(\.\d+)?$/.test(String(pValue)))
	{
		return Number(pValue);
	}
	return pValue;
}

/**
 * Build an action from an element's `data-hl-action` and `data-*` attributes.
 *
 * @param {{ dataset: Record<string, string> }} pElement
 * @return {DashboardAction|null}
 */
function actionFromElement(pElement)
{
	if (!pElement || !pElement.dataset || !pElement.dataset.hlAction)
	{
		return null;
	}
	/** @type {Record<string, any>} */
	const tmpAction = { Action: pElement.dataset.hlAction };
	for (const [ tmpKey, tmpValue ] of Object.entries(pElement.dataset))
	{
		if (tmpKey === 'hlAction')
		{
			continue;
		}
		const tmpName = normalizeParamName(tmpKey);
		const tmpNormalized = normalizeParamValue(tmpName, tmpValue);
		if (tmpNormalized !== undefined)
		{
			tmpAction[tmpName] = tmpNormalized;
		}
	}
	return /** @type {DashboardAction} */ (tmpAction);
}

/**
 * The legacy app_dashboards materials samples dashboard posts a bare `{ IDProject, IDLineItem, IDMaterial, Quantity,
 * Type }`; read it as Sample.Create.
 *
 * @param {Record<string, any>} pPayload
 * @return {DashboardAction|null}
 */
function actionFromLegacySamplePayload(pPayload)
{
	if (!pPayload || typeof pPayload !== 'object' || pPayload.Action || !pPayload.IDProject || !pPayload.IDLineItem || !pPayload.IDMaterial)
	{
		return null;
	}
	/** @type {Record<string, any>} */
	const tmpAction = { Action: 'Sample.Create', IDProject: Number(pPayload.IDProject), IDLineItem: Number(pPayload.IDLineItem), IDMaterial: Number(pPayload.IDMaterial) };
	if (pPayload.Quantity !== undefined && pPayload.Quantity !== null && pPayload.Quantity !== '')
	{
		tmpAction.Quantity = Number(pPayload.Quantity);
	}
	if (pPayload.Type)
	{
		tmpAction.SampleType = pPayload.Type;
	}
	return /** @type {DashboardAction} */ (tmpAction);
}

/**
 * @param {DashboardAction} pAction
 * @return {{ Valid: boolean, Reason?: string }}
 */
function validateAction(pAction)
{
	const tmpDefinition = pAction && ACTIONS[pAction.Action];
	if (!tmpDefinition)
	{
		return { Valid: false, Reason: `unknown action ${pAction && pAction.Action}` };
	}
	const tmpMissing = tmpDefinition.Required.filter((pName) => pAction[pName] === undefined || pAction[pName] === null || pAction[pName] === '' || pAction[pName] === 0);
	if (tmpMissing.length > 0)
	{
		return { Valid: false, Reason: `${pAction.Action} needs ${tmpMissing.join(', ')}` };
	}
	return { Valid: true };
}

/**
 * The action bus. A host registers a handler per action; anything it doesn't handle goes to the fallback, then to the
 * configured transport (the iPad app, or the page that framed this one).
 */
class HeadlightDashboardActions extends libFableServiceProviderBase
{
	/**
	 * @param {any} pFable
	 * @param {Record<string, any>} [pOptions] - `Transport`: 'native' | 'parent' | null; `ParentOrigin` for 'parent';
	 *   `TransportActions`: the actions whoever framed this page can take (null: all of them).
	 * @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({ Transport: null, ParentOrigin: null, TransportActions: null }, pOptions), pServiceHash);
		/** @type {any} */
		this.log;
		/** @type {Record<string, (pAction: DashboardAction) => any>} */
		this.handlers = {};
		/** @type {((pAction: DashboardAction) => any)|null} */
		this.fallback = null;
		this._boundContainers = new WeakSet();
	}

	/**
	 * @param {string} pAction - A key of ACTIONS.
	 * @param {(pAction: DashboardAction) => any} fHandler
	 */
	registerHandler(pAction, fHandler)
	{
		if (!ACTIONS[pAction])
		{
			this.log.warn(`HeadlightDashboardActions: registering a handler for unknown action ${pAction}.`);
		}
		this.handlers[pAction] = fHandler;
	}

	/**
	 * @param {(pAction: DashboardAction) => any} fHandler
	 */
	registerFallback(fHandler)
	{
		this.fallback = fHandler;
	}

	/**
	 * @param {'native'|'parent'|null} pTransport
	 * @param {string} [pParentOrigin] - Required for 'parent': where actions may be posted.
	 * @param {Array<string>|null} [pTransportActions] - What the framing app can take; null for all.
	 */
	setTransport(pTransport, pParentOrigin, pTransportActions)
	{
		this.options.Transport = pTransport;
		this.options.ParentOrigin = pParentOrigin || null;
		this.options.TransportActions = Array.isArray(pTransportActions) ? pTransportActions : null;
	}

	/**
	 * Can anything here take this action? Dashboards hide what can't be done — "Take a sample" in an app with no way to
	 * create one, or on an iPad project with no sample creation form.
	 *
	 * @param {string} pAction
	 * @return {boolean}
	 */
	isAvailable(pAction)
	{
		if (typeof this.handlers[pAction] === 'function' || typeof this.fallback === 'function')
		{
			return true;
		}
		if (this.options.Transport === 'native' || (this.options.Transport === 'parent' && this.options.ParentOrigin))
		{
			return !Array.isArray(this.options.TransportActions) || this.options.TransportActions.includes(pAction);
		}
		return false;
	}

	/**
	 * @param {string} pScopeSelector - The container the rule applies inside.
	 * @return {string} CSS hiding every element whose action is unavailable here ('' when all are available)
	 */
	unavailableActionsCSS(pScopeSelector)
	{
		return Object.keys(ACTIONS)
			.filter((pAction) => !this.isAvailable(pAction))
			.map((pAction) => `${pScopeSelector} [data-hl-action="${pAction}"] { display: none !important; }`)
			.join('\n');
	}

	/**
	 * @param {DashboardAction} pAction
	 * @return {boolean} true when something took the action
	 */
	dispatch(pAction)
	{
		const tmpValidity = validateAction(pAction);
		if (!tmpValidity.Valid)
		{
			this.log.warn(`HeadlightDashboardActions: ignored (${tmpValidity.Reason}).`, pAction);
			return false;
		}
		const tmpHandler = this.handlers[pAction.Action];
		if (typeof tmpHandler === 'function')
		{
			tmpHandler(pAction);
			return true;
		}
		if (this._sendThroughTransport(pAction))
		{
			return true;
		}
		if (typeof this.fallback === 'function')
		{
			this.fallback(pAction);
			return true;
		}
		this.log.warn(`HeadlightDashboardActions: nothing handles ${pAction.Action} in this app.`, pAction);
		return false;
	}

	/**
	 * @param {DashboardAction} pAction
	 * @return {boolean}
	 */
	_sendThroughTransport(pAction)
	{
		if (typeof window === 'undefined')
		{
			return false;
		}
		if (this.options.Transport === 'native')
		{
			const tmpBridge = window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[NATIVE_HANDLER];
			if (tmpBridge && typeof tmpBridge.postMessage === 'function')
			{
				tmpBridge.postMessage(pAction);
				return true;
			}
			return false;
		}
		if (this.options.Transport === 'parent' && this.options.ParentOrigin && window.parent && window.parent !== window)
		{
			window.parent.postMessage({ [PARENT_MESSAGE_KEY]: pAction }, this.options.ParentOrigin);
			return true;
		}
		return false;
	}

	/**
	 * Turn clicks on `[data-hl-action]` inside a container into actions. One listener per container, however often its
	 * content re-renders.
	 *
	 * @param {Element} pContainer
	 */
	bindContainer(pContainer)
	{
		if (!pContainer || this._boundContainers.has(pContainer))
		{
			return;
		}
		this._boundContainers.add(pContainer);
		pContainer.addEventListener('click', (pEvent) =>
		{
			const tmpTarget = /** @type {Element} */ (pEvent.target);
			const tmpElement = tmpTarget && typeof tmpTarget.closest === 'function' ? tmpTarget.closest('[data-hl-action]') : null;
			// Containers nest (a dashboard frame inside a host page); one click is one action.
			if (!tmpElement || !pContainer.contains(tmpElement) || /** @type {any} */ (pEvent).headlightDashboardActionHandled)
			{
				return;
			}
			/** @type {any} */ (pEvent).headlightDashboardActionHandled = true;
			pEvent.preventDefault();
			const tmpAction = actionFromElement(/** @type {any} */ (tmpElement));
			if (tmpAction)
			{
				this.dispatch(tmpAction);
			}
		});
	}
}

module.exports = HeadlightDashboardActions;
module.exports.default_configuration = { ProviderIdentifier: 'HeadlightDashboardActions' };
module.exports.ACTIONS = ACTIONS;
module.exports.NATIVE_HANDLER = NATIVE_HANDLER;
module.exports.PARENT_MESSAGE_KEY = PARENT_MESSAGE_KEY;
module.exports.actionFromElement = actionFromElement;
module.exports.actionFromLegacySamplePayload = actionFromLegacySamplePayload;
module.exports.validateAction = validateAction;

/**
 * Read an `actions=Sample.Create,Document.Open` list (from the framing app) into known action names.
 *
 * @param {string|null|undefined} pValue
 * @return {Array<string>|null} null when absent (the framing app takes every action)
 */
module.exports.parseActionList = (pValue) =>
{
	if (pValue === null || pValue === undefined)
	{
		return null;
	}
	return String(pValue).split(',').map((pName) => pName.trim()).filter((pName) => ACTIONS[pName]);
};
